import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { usePostHog } from '@posthog/react';
import { useTranslation } from '../../hooks/useTranslation';
import { isAnalyticsConsentGiven } from '../../utils/onetrust/isAnalyticsConsentGiven';

type PendingPageView = { url: string; pathname: string };

export const PostHogPageView = () => {
  const location = useLocation();
  const posthog = usePostHog();
  const { locale } = useTranslation();
  const pending = useRef<PendingPageView[]>([]);

  useEffect(() => {
    if (locale) posthog.register({ locale });
  }, [locale, posthog]);

  /**
   * This effect captures a pageview event on initial load and whenever the location changes.
   * If the user has given consent for analytics cookies, the pageview is sent to PostHog immediately.
   * If not, the pageview is added to a pending queue to be flushed later if/when consent is given.
   *
   * The pageview event includes the full URL and pathname as properties.
   */
  useEffect(() => {
    const pageview: PendingPageView = {
      url: window.location.href,
      pathname: `${window.location.pathname}${window.location.search}`,
    };

    if (isAnalyticsConsentGiven(window.OnetrustActiveGroups?.split(','))) {
      posthog.capture('$pageview', { $current_url: pageview.url, $pathname: pageview.pathname });
    } else {
      pending.current.push(pageview);
    }
  }, [location, posthog]);

  /**
   * This effect overrides the global `OptanonWrapper` function, which is called by the OneTrust script whenever the user's cookie consent preferences change. By wrapping this function, we can check if analytics consent has been given and flush any pending pageview events to PostHog immediately after consent is granted, without waiting for another location change.
   *
   * The original `OptanonWrapper` function is preserved and called within our wrapper to ensure that any existing functionality tied to it continues to work as expected. We also clean up by restoring the original function when the component unmounts.
   *
   * Note that we check for analytics consent within the wrapper function using the same `isAnalyticsConsentGiven` utility, which inspects the `OnetrustActiveGroups` string injected by the OneTrust script. If consent is given and there are pending pageview events, we iterate over them and send each one to PostHog before clearing the pending queue.
   */
  useEffect(() => {
    const prev = window.OptanonWrapper;
    window.OptanonWrapper = () => {
      prev?.();
      if (
        isAnalyticsConsentGiven(window.OnetrustActiveGroups?.split(',')) &&
        pending.current.length > 0
      ) {
        pending.current.forEach((pv) =>
          posthog.capture('$pageview', { $current_url: pv.url, $pathname: pv.pathname }),
        );
        pending.current = [];
      }
    };
    return () => {
      window.OptanonWrapper = prev;
    };
  }, [posthog]);

  return null;
};
